<template>
<div class="pl-mwrap">
    <div class="pl-mgrid" :style="gridStyle">
        <div class="pl-mhd pl-mhd-first">{{ monthLabel }}</div>
        <div v-for="date in board.dates" :key="'h' + date"
             class="pl-mhd"
             :class="{ 'pl-mtoday': date === board.today, 'pl-mweekend': isWeekend(date) }">
            {{ dayNum(date) }}<small>{{ dayName(date) }}</small>
        </div>

        <template v-for="person in board.people">
            <div class="pl-mperson" :key="'p' + person.id">
                <span class="pl-av-sm">{{ initials(person.name) }}</span>
                <span class="pl-mperson-nm">{{ person.name }}</span>
            </div>
            <div v-for="date in board.dates" :key="person.id + date"
                 class="pl-mcell"
                 :class="{ 'pl-mtoday-cell': date === board.today, 'pl-mweekend': isWeekend(date) }"
                 :title="person.name + ' · ' + date + ' · ' + pct(person, date) + '%'"
                 @click="$emit('goto-day', date)">
                <span class="pl-blk" :class="blockClass(person, date)">
                    <template v-if="pct(person, date) > 150">!</template>
                </span>
            </div>
        </template>
    </div>

    <div class="pl-mlegend">
        <b>Load per person per day:</b>
        <span><i style="background:#f4f3f0"></i>Nothing planned</span>
        <span><i style="background:#0ca30c;opacity:.28"></i>Light (≤50%)</span>
        <span><i style="background:#0ca30c;opacity:.75"></i>Planned (≤100%)</span>
        <span><i style="background:#fab219"></i>Tight (≤150%)</span>
        <span><i style="background:#d03b3b"></i>Overloaded ( ! )</span>
        <span class="pl-mlegend-hint">Click any block → that day's Day view</span>
    </div>
</div>
</template>

<script>
import { adjustedPct, initials } from './shared';

export default {
    name: 'MonthView',

    props: {
        board: { type: Object, required: true },
    },

    computed: {
        gridStyle() {
            return { gridTemplateColumns: '148px repeat(' + this.board.dates.length + ', 1fr)' };
        },
        monthLabel() {
            return new Date(this.board.dates[0]).toLocaleDateString('en-GB', { month: 'long' });
        },
    },

    methods: {
        initials,

        planned(person, date) {
            return this.board.slots
                .filter(s => s.user_id === person.id && s.date === date)
                .reduce((sum, s) => sum + (s.planned_seconds || 0), 0);
        },
        capacity(person, date) {
            return Math.round(((person.hours || {})[date] || 0) * 3600);
        },
        pct(person, date) {
            return adjustedPct(this.planned(person, date), this.capacity(person, date), this.board.efficiency_percent);
        },
        blockClass(person, date) {
            const planned = this.planned(person, date);
            if (planned <= 0) return 'pl-blk-none';
            const pct = this.pct(person, date);
            if (this.capacity(person, date) <= 0) return 'pl-blk-tight';   // work planned on a no-hours day
            if (pct <= 50) return 'pl-blk-low';
            if (pct <= 100) return 'pl-blk-ok';
            if (pct <= 150) return 'pl-blk-tight';
            return 'pl-blk-over';
        },
        dayNum(date) {
            return new Date(date).getDate();
        },
        dayName(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short' });
        },
        isWeekend(date) {
            const n = new Date(date).getDay();
            return n === 0 || n === 6;
        },
    },
};
</script>

<style scoped>
.pl-mwrap{overflow-x:auto}
.pl-mgrid{display:grid; min-width:980px;
    background:#fcfcfb; border:1px solid #e1e0d9; border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); overflow:hidden}
.pl-mhd{background:#f4f3f0; font-size:11.5px; font-weight:700; color:#52514e;
    padding:7px 3px; text-align:center; border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9}
.pl-mhd small{display:block; color:#898781; font-weight:600}
.pl-mhd-first{text-align:left; padding-left:12px; font-size:13px}
.pl-mtoday{background:#0b0b0b; color:#fff}
.pl-mtoday small{color:#c9c8c0}
.pl-mhd.pl-mweekend, .pl-mcell.pl-mweekend{opacity:.55}
.pl-mperson{padding:8px 12px; display:flex; align-items:center; gap:9px; background:#f4f3f0;
    border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9}
.pl-av-sm{width:30px; height:30px; border-radius:50%; background:#0b0b0b; color:#fff;
    display:flex; align-items:center; justify-content:center; font-weight:800; font-size:12px; flex:none}
.pl-mperson-nm{font-weight:700; font-size:13.5px}
.pl-mcell{height:44px; border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9;
    padding:5px; background:#fcfcfb; cursor:pointer}
.pl-mtoday-cell{background:linear-gradient(rgba(11,11,11,.05),rgba(11,11,11,.05))}
.pl-blk{display:flex; align-items:center; justify-content:center; width:100%; height:100%;
    border-radius:6px; color:#fff; font-weight:900; font-size:12px}
.pl-blk-none{background:#f4f3f0}
.pl-blk-low{background:#0ca30c; opacity:.28}
.pl-blk-ok{background:#0ca30c; opacity:.75}
.pl-blk-tight{background:#fab219}
.pl-blk-over{background:#d03b3b}
.pl-mlegend{display:flex; gap:16px; align-items:center; margin-top:10px; font-size:12.5px;
    font-weight:600; color:#52514e; flex-wrap:wrap}
.pl-mlegend span{display:flex; align-items:center; gap:6px}
.pl-mlegend i{width:15px; height:15px; border-radius:5px; display:inline-block}
.pl-mlegend-hint{color:#898781}
</style>
